import type { SelectChangeEvent } from '@mui/material';
import { InputLabel, MenuItem, Select } from '@mui/material';
import { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import type { InstrumentType } from '../../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import { TranscriptionDialogContext } from '../../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import { CompactMusicalKeys } from '../../../../../utils';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '.5em',
    gap: '.5em',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  text: {
    fontSize: '13px',
  },
}));

const AdvancedSettingsParams = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { settings, setSettings } = useContext(TranscriptionDialogContext);
  const [tempo, setTempo] = useState<string>('null');

  useEffect(() => {
    if (settings.tempo) {
      setTempo(settings.tempo);
    }
    if (settings.exact_tempo) {
      if (settings.exact_tempo >= 50 && settings.exact_tempo < 100) {
        setTempo('80');
      } else if (settings.exact_tempo >= 100 && settings.exact_tempo < 150) {
        setTempo('130');
      } else if (settings.exact_tempo >= 150 && settings.exact_tempo <= 200) {
        setTempo('180');
      } else if (settings.exact_tempo > 200) {
        setTempo('230');
      }
    }
  }, [settings.tempo, settings.exact_tempo, setSettings]);

  const handleTempoChange = (evt: SelectChangeEvent<string>) => {
    setSettings({ exact_tempo: undefined });
    setSettings({ tempo: evt.target.value });
  };

  const handleChange = (
    evt: SelectChangeEvent<string | boolean | number>,
    settingsInstrument?: InstrumentType,
  ) => {
    setSettings({ [evt.target.name]: evt.target.value }, settingsInstrument);
  };
  return (
    <div className={classes.container}>
      {(!settings.instruments.includes('drums') || settings.instruments.length > 1) && (
        <div>
          <InputLabel className={classes.text}>{t('home.paramsdialog.musicalkey')}</InputLabel>
          <Select
            className={classes.text}
            fullWidth
            label={t('home.paramsdialog.musicalkey')}
            name="key"
            variant="standard"
            value={settings?.key ?? 'null'}
            onChange={(evt) => handleChange(evt)}
          >
            <MenuItem value="null">{t('home.paramsdialog.detect')}</MenuItem>
            {CompactMusicalKeys.map((v) => (
              <MenuItem key={v.label} value={v.value}>
                {v.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      )}
      <div>
        <InputLabel className={classes.text}>{t('home.paramsdialog.timesig')}</InputLabel>
        <Select
          className={classes.text}
          fullWidth
          label={t('home.paramsdialog.timesig')}
          name="time_signature"
          variant="standard"
          value={settings?.time_signature ? settings?.time_signature : 'null'}
          onChange={(evt) => handleChange(evt)}
        >
          <MenuItem value="null">{t('home.paramsdialog.detect')}</MenuItem>
          <MenuItem value="4/4">4/4</MenuItem>
          <MenuItem value="3/4">3/4</MenuItem>
        </Select>
      </div>
      <div>
        <InputLabel className={classes.text}>{t('home.paramsdialog.tempo')}</InputLabel>
        <Select
          fullWidth
          className={classes.text}
          variant="standard"
          label={t('home.paramsdialog.tempo')}
          name="tempo"
          value={tempo}
          onChange={(evt) => handleTempoChange(evt)}
        >
          <MenuItem value="null">{t('home.paramsdialog.detect')}</MenuItem>
          <MenuItem value={80}>Andante (50 - 110 BPM)</MenuItem>
          <MenuItem value={130}>Moderato (100 - 160 BPM)</MenuItem>
          <MenuItem value={180}>Presto (150 - 210 BPM)</MenuItem>
          <MenuItem value={230}>Prestissimo (200 - 260 BPM)</MenuItem>
        </Select>
      </div>

      <div>
        <InputLabel className={classes.text}>{t('home.paramsdialog.quantisation')}</InputLabel>
        <Select
          fullWidth
          className={classes.text}
          variant="standard"
          label={t('home.paramsdialog.quantisation')}
          name="min_duration"
          value={settings.min_duration ?? 0.0625}
          onChange={(evt) => handleChange(evt)}
        >
          <MenuItem value={0.25}>{t('home.paramsdialog.quarter')}</MenuItem>
          <MenuItem value={0.125}>{t('home.paramsdialog.eighth')}</MenuItem>
          <MenuItem value={0.0625}>{t('home.paramsdialog.sixteenth')}</MenuItem>
        </Select>
      </div>
      <div>
        <InputLabel className={classes.text}>
          {t('home.paramsdialog.triplets_recognition')}
        </InputLabel>
        <Select
          fullWidth
          className={classes.text}
          variant="standard"
          label={t('home.paramsdialog.triplets_recognition')}
          name="triplets"
          value={!!settings.triplets}
          onChange={(evt) => handleChange(evt)}
        >
          {/* @ts-ignore */}
          <MenuItem value>{t('home.paramsdialog.yes')}</MenuItem>
          {/* @ts-ignore */}
          <MenuItem value={false}>{t('home.paramsdialog.no')}</MenuItem>
        </Select>
      </div>
      {settings.instruments.includes('piano') && (
        <div>
          <InputLabel className={classes.text}>{t('home.paramsdialog.hand_separation')}</InputLabel>
          <Select
            fullWidth
            className={classes.text}
            variant="standard"
            label={t('home.paramsdialog.hand_separation')}
            name="hand_separation"
            value={settings.piano?.hand_separation ?? 'null'}
            onChange={(evt) => handleChange(evt, 'piano')}
          >
            <MenuItem value="null">{t('home.paramsdialog.detect_hands')}</MenuItem>
            <MenuItem value="split=60">{t('home.paramsdialog.split_hands_60')}</MenuItem>
            <MenuItem value="single">{t('home.paramsdialog.single_handed')}</MenuItem>
          </Select>
        </div>
      )}
      {settings.instruments.includes('piano') && (
        <div>
          <InputLabel className={classes.text}>{t('home.paramsdialog.detect_clef')}</InputLabel>
          <Select
            fullWidth
            className={classes.text}
            variant="standard"
            label={t('home.paramsdialog.detect_clef')}
            name="clef_changes"
            value={!!settings.piano?.clef_changes}
            onChange={(evt) => handleChange(evt, 'piano')}
          >
            {/* @ts-ignore */}
            <MenuItem value>{t('home.paramsdialog.yes')}</MenuItem>
            {/* @ts-ignore */}
            <MenuItem value={false}>{t('home.paramsdialog.no')}</MenuItem>
          </Select>
        </div>
      )}
      {settings.instruments.includes('vocals') && (
        <div>
          <InputLabel className={classes.text}>{t('home.paramsdialog.voice_type')}</InputLabel>
          <Select
            fullWidth
            variant="standard"
            className={classes.text}
            label={t('home.paramsdialog.voice_type')}
            name="voice_type"
            value={settings.vocals?.voice_type ?? 'null'}
            onChange={(evt) => handleChange(evt, 'vocals')}
          >
            <MenuItem value="null">{t('home.paramsdialog.voice_type_detect')}</MenuItem>
            <MenuItem value="soprano">{t('home.paramsdialog.voice_type_soprano')}</MenuItem>
            <MenuItem value="alto">{t('home.paramsdialog.voice_type_alto')}</MenuItem>
            <MenuItem value="tenor">{t('home.paramsdialog.voice_type_tenor')}</MenuItem>
            <MenuItem value="bass">{t('home.paramsdialog.voice_type_bass')}</MenuItem>
          </Select>
        </div>
      )}
    </div>
  );
};

export default AdvancedSettingsParams;
